import React from "react"
import {
	Flex,
	Text,
	Accordion,
	Icon,
	Rte,
	IconTypes,
} from "@capioweb/ui-components"
import { XhtmlString, Price } from "../../@types/CapioWeb/core"
import {
	StringProperty,
	NumberProperty,
	LinkProperty,
} from "../../@types/EpiFoundationSpa/Property"
import { createMarkup } from "../../utils"
import { LocalesContext } from "../../context"
import { urlIsInternal } from "../../utils/url"
import { sendAccordionClickDataLayerPush } from "../../utils/piwikPro"
import {
	DynamicLink,
	HEADER_HEIGHT_S,
	scale,
} from "@capioweb/ui-components/src"

export interface OurOfferAccordionBaseBlockItem {
	heading: StringProperty
	icon: StringProperty
	localInformation: XhtmlString
	generalInfoTop: XhtmlString
	offerType: NumberProperty
	linkItem: LinkProperty
	priceTitle: StringProperty
	prices: Price[]
	anchorId?: StringProperty
}

interface OurOfferAccordionItemProps extends OurOfferAccordionBaseBlockItem {
	hidden?: boolean
	activeId?: string
	setActiveId?: (id: string) => void
}

const OurOfferAccordionItem: React.FC<OurOfferAccordionItemProps> = (props) => {
	const { translate, translationKeys } = React.useContext(LocalesContext)
	const id = props.anchorId.value || props.heading.value

	const ref = React.useRef(null)

	const scrollToRef = () => {
		window.scrollTo(0, ref.current.offsetTop)
		setTimeout(() => {
			var headerOffset = scale(HEADER_HEIGHT_S) + 10
			var elementPosition = ref.current.getBoundingClientRect().top
			var offsetPosition = elementPosition + window.pageYOffset - headerOffset

			window.scrollTo({
				top: offsetPosition,
				behavior: "smooth",
			})
		}, 100)
	}

	return (
		props.heading?.value && (
			<Flex
				ref={ref}
				id={id}
				w={1}
				flexDirection="column"
				display={props.hidden ? "none" : "flex"}
			>
				<Accordion.Header
					id={id}
					toggleLabel={`${translate(translationKeys.open)} ${
						props.heading.value
					}`}
					onClick={() => {
						props.setActiveId &&
							props.setActiveId(props.activeId === id ? null : id)
						scrollToRef()
						props.activeId !== id &&
							sendAccordionClickDataLayerPush(props.heading.value)
					}}
				>
					{props.icon && (
						<Icon
							icon={props.icon.value as IconTypes}
							size={4.75}
							ml={-0.875}
							my={-0.5}
						/>
					)}
					<Text
						as="h3"
						variant="headline-3"
						mb={0}
						truncate="singleLine"
						pl={1}
						pr={4}
						ml={props.icon ? 0 : 3.875}
					>
						{props.heading?.value}
					</Text>
				</Accordion.Header>
				<Accordion.Panel id={id}>
					<Flex
						flexDirection="column"
						pt={3}
						pb={5}
						px={{ _: 2, m: 6.5, l: 7.5 }}
					>
						<Rte>
							{props.generalInfoTop.value && (
								<div
									dangerouslySetInnerHTML={createMarkup(
										props.generalInfoTop.value
									)}
								/>
							)}
							{props.localInformation.value && (
								<div
									dangerouslySetInnerHTML={createMarkup(
										props.localInformation.value
									)}
								/>
							)}

							{props.linkItem &&
								props.linkItem &&
								props.linkItem.href &&
								(urlIsInternal(props.linkItem.href) ? (
									<Text.Link
										key={props.linkItem.href}
										variant="paragraph-16"
										style={{ display: "block" }}
										to={props.linkItem?.href ?? "#"}
									>
										{props.linkItem.text}
									</Text.Link>
								) : (
									<Text.Anchor
										key={props.linkItem.href}
										variant="paragraph-16"
										style={{ display: "block" }}
										href={props.linkItem.href}
									>
										{props.linkItem.text}
									</Text.Anchor>
								))}

							{props.priceTitle.value !== "" && (
								<Text
									variant="paragraph-16"
									as="h4"
									mt={4}
									mb={0}
									pr={4}
									fontWeight="bold"
								>
									{props.priceTitle.value}
								</Text>
							)}
							{props.prices.length > 0 && (
								<Flex
									flexDirection="column"
									as="ul"
									style={{ margin: 0, padding: 0 }}
								>
									{props.prices.map((item, i) => (
										<li style={{ listStyle: "none" }} key={item.url + i}>
											<Flex justifyContent="space-between">
												{item.url ? (
													<DynamicLink url={item?.url ?? "#"}>
														{item.linkText}
													</DynamicLink>
												) : (
													<Flex>{item.linkText}</Flex>
												)}
												<Flex minw={10} justifyContent="right">
													{item.price}
												</Flex>
											</Flex>
										</li>
									))}
								</Flex>
							)}
						</Rte>
					</Flex>
				</Accordion.Panel>
			</Flex>
		)
	)
}

export { OurOfferAccordionItem }
